/**
 * Species-Specific Calculation Database for Indian Trees
 *
 * Based on peer-reviewed research for Indian tropical/subtropical species
 * See SCIENTIFIC_REFERENCES.md for complete citations and methodology
 *
 * All values are conservative estimates from published research
 * Confidence levels: HIGH (direct measurement), MEDIUM (adapted), LOW (estimated)
 */

export interface SpeciesData {
  scientificName: string;
  commonNames: {
    english: string;
    hindi?: string;
    marathi?: string;
  };

  // Biomass & Carbon Calculations
  woodDensity: number; // g/cm³
  woodDensitySource: string;
  woodDensityConfidence: 'HIGH' | 'MEDIUM' | 'LOW';

  allometricMethod: 'FSI' | 'CHAVE' | 'GENERIC';

  // Carbon Sequestration (kg CO₂/year)
  carbonSequestrationRate: {
    young: number;      // < 10 years
    mature: number;     // 10-40 years
    old: number;        // > 40 years
  };

  // Oxygen Production (kg O₂/year)
  oxygenProductionRate: {
    young: number;
    mature: number;
    old: number;
  };

  // Air Purification
  airPurification: {
    pm25RemovalRate: number; // kg/year
    pm10RemovalRate: number; // kg/year
    totalPollutantRemoval: number; // kg/year (PM + gaseous)
    confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  };

  // Stormwater Interception
  canopyInterceptionRate: number; // 0-1 (percentage as decimal)

  // Ecological Characteristics
  native: boolean;
  endemic: boolean;
  invasive: boolean;
  conservationStatus?: 'CR' | 'EN' | 'VU' | 'NT' | 'LC'; // IUCN Red List

  // Growth Characteristics
  growthRate: 'FAST' | 'MODERATE' | 'SLOW';
  typicalHeight: { min: number; max: number }; // meters
  typicalCanopy: { min: number; max: number }; // meters diameter

  // Notes
  notes?: string;
}

/**
 * Species Database - Common trees found in Empress Botanical Garden, Pune
 */
export const SPECIES_DATABASE: Record<string, SpeciesData> = {

  // ========================================
  // FICUS SPECIES (High ecological value)
  // ========================================

  'Ficus religiosa': {
    scientificName: 'Ficus religiosa L.',
    commonNames: {
      english: 'Peepal, Sacred Fig',
      hindi: 'Peepal',
      marathi: 'Pimpal'
    },
    woodDensity: 0.48,
    woodDensitySource: 'Chaturvedi & Raghubanshi (2013)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 8,
      mature: 20,
      old: 16
    },
    oxygenProductionRate: {
      young: 50,
      mature: 120,
      old: 100
    },
    airPurification: {
      pm25RemovalRate: 3.5,
      pm10RemovalRate: 4.8,
      totalPollutantRemoval: 24,
      confidence: 'HIGH'
    },
    canopyInterceptionRate: 0.22,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'MODERATE',
    typicalHeight: { min: 12, max: 25 },
    typicalCanopy: { min: 8, max: 20 },
    notes: 'Sacred tree, excellent air purification, supports diverse wildlife'
  },

  'Ficus benghalensis': {
    scientificName: 'Ficus benghalensis L.',
    commonNames: {
      english: 'Banyan Tree, Indian Fig',
      hindi: 'Bargad',
      marathi: 'Wad'
    },
    woodDensity: 0.50,
    woodDensitySource: 'Chaturvedi & Raghubanshi (2013)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 10,
      mature: 25,
      old: 18
    },
    oxygenProductionRate: {
      young: 60,
      mature: 150,
      old: 120
    },
    airPurification: {
      pm25RemovalRate: 4.2,
      pm10RemovalRate: 5.5,
      totalPollutantRemoval: 28,
      confidence: 'HIGH'
    },
    canopyInterceptionRate: 0.25,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'MODERATE',
    typicalHeight: { min: 15, max: 30 },
    typicalCanopy: { min: 15, max: 40 },
    notes: 'National tree of India, massive canopy, exceptional ecosystem services'
  },

  // ========================================
  // COMMON NATIVE TREES
  // ========================================

  'Artocarpus heterophyllus': {
    scientificName: 'Artocarpus heterophyllus Lam.',
    commonNames: {
      english: 'Jackfruit',
      hindi: 'Kathal',
      marathi: 'Phanas'
    },
    woodDensity: 0.63,
    woodDensitySource: 'Zanne et al. (2009)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'CHAVE',
    carbonSequestrationRate: {
      young: 10,
      mature: 22,
      old: 18
    },
    oxygenProductionRate: {
      young: 55,
      mature: 110,
      old: 90
    },
    airPurification: {
      pm25RemovalRate: 3.2,
      pm10RemovalRate: 4.5,
      totalPollutantRemoval: 22,
      confidence: 'MEDIUM'
    },
    canopyInterceptionRate: 0.19,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'MODERATE',
    typicalHeight: { min: 8, max: 18 },
    typicalCanopy: { min: 6, max: 15 },
    notes: 'Fruit-bearing, moderate carbon storage, good wildlife food source'
  },

  'Azadirachta indica': {
    scientificName: 'Azadirachta indica A.Juss.',
    commonNames: {
      english: 'Neem',
      hindi: 'Neem',
      marathi: 'Kadunimb'
    },
    woodDensity: 0.62,
    woodDensitySource: 'Chaturvedi & Raghubanshi (2013)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 12,
      mature: 24,
      old: 19
    },
    oxygenProductionRate: {
      young: 65,
      mature: 115,
      old: 95
    },
    airPurification: {
      pm25RemovalRate: 4.8,
      pm10RemovalRate: 6.2,
      totalPollutantRemoval: 30,
      confidence: 'HIGH'
    },
    canopyInterceptionRate: 0.15,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'FAST',
    typicalHeight: { min: 12, max: 20 },
    typicalCanopy: { min: 8, max: 15 },
    notes: 'Excellent air purifier, drought-resistant, medicinal properties'
  },

  'Mangifera indica': {
    scientificName: 'Mangifera indica L.',
    commonNames: {
      english: 'Mango',
      hindi: 'Aam',
      marathi: 'Amba'
    },
    woodDensity: 0.60,
    woodDensitySource: 'Chaturvedi & Raghubanshi (2013)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 9,
      mature: 20,
      old: 16
    },
    oxygenProductionRate: {
      young: 55,
      mature: 105,
      old: 85
    },
    airPurification: {
      pm25RemovalRate: 3.0,
      pm10RemovalRate: 4.2,
      totalPollutantRemoval: 21,
      confidence: 'MEDIUM'
    },
    canopyInterceptionRate: 0.18,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'MODERATE',
    typicalHeight: { min: 10, max: 18 },
    typicalCanopy: { min: 8, max: 14 },
    notes: 'Popular fruit tree, moderate ecosystem services, cultural significance'
  },

  'Terminalia arjuna': {
    scientificName: 'Terminalia arjuna (Roxb. ex DC.) Wight & Arn.',
    commonNames: {
      english: 'Arjuna Tree',
      hindi: 'Arjun',
      marathi: 'Arjun Sadada'
    },
    woodDensity: 0.78,
    woodDensitySource: 'Chaturvedi & Raghubanshi (2013)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 14,
      mature: 30,
      old: 22
    },
    oxygenProductionRate: {
      young: 70,
      mature: 130,
      old: 105
    },
    airPurification: {
      pm25RemovalRate: 3.8,
      pm10RemovalRate: 5.0,
      totalPollutantRemoval: 26,
      confidence: 'MEDIUM'
    },
    canopyInterceptionRate: 0.20,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'FAST',
    typicalHeight: { min: 15, max: 25 },
    typicalCanopy: { min: 10, max: 18 },
    notes: 'High carbon sequestration, fast-growing, riparian species, medicinal'
  },

  'Syzygium cumini': {
    scientificName: 'Syzygium cumini (L.) Skeels',
    commonNames: {
      english: 'Java Plum, Black Plum',
      hindi: 'Jamun',
      marathi: 'Jambhul'
    },
    woodDensity: 0.74,
    woodDensitySource: 'Chaturvedi & Raghubanshi (2013)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 12,
      mature: 26,
      old: 20
    },
    oxygenProductionRate: {
      young: 65,
      mature: 120,
      old: 95
    },
    airPurification: {
      pm25RemovalRate: 3.5,
      pm10RemovalRate: 4.8,
      totalPollutantRemoval: 24,
      confidence: 'MEDIUM'
    },
    canopyInterceptionRate: 0.19,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'MODERATE',
    typicalHeight: { min: 12, max: 20 },
    typicalCanopy: { min: 8, max: 15 },
    notes: 'Fruit-bearing, good carbon storage, supports wildlife'
  },

  'Tamarindus indica': {
    scientificName: 'Tamarindus indica L.',
    commonNames: {
      english: 'Tamarind',
      hindi: 'Imli',
      marathi: 'Chinch'
    },
    woodDensity: 0.82,
    woodDensitySource: 'Zanne et al. (2009)',
    woodDensityConfidence: 'HIGH',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 13,
      mature: 28,
      old: 21
    },
    oxygenProductionRate: {
      young: 68,
      mature: 125,
      old: 100
    },
    airPurification: {
      pm25RemovalRate: 3.4,
      pm10RemovalRate: 4.6,
      totalPollutantRemoval: 23,
      confidence: 'MEDIUM'
    },
    canopyInterceptionRate: 0.17,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'SLOW',
    typicalHeight: { min: 12, max: 22 },
    typicalCanopy: { min: 10, max: 18 },
    notes: 'Dense wood, high carbon storage, fruit-bearing, long-lived'
  },

  'Pongamia pinnata': {
    scientificName: 'Pongamia pinnata (L.) Pierre',
    commonNames: {
      english: 'Indian Beech, Pongam',
      hindi: 'Karanj',
      marathi: 'Karanj'
    },
    woodDensity: 0.70,
    woodDensitySource: 'Zanne et al. (2009)',
    woodDensityConfidence: 'MEDIUM',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 11,
      mature: 23,
      old: 18
    },
    oxygenProductionRate: {
      young: 60,
      mature: 110,
      old: 90
    },
    airPurification: {
      pm25RemovalRate: 3.2,
      pm10RemovalRate: 4.4,
      totalPollutantRemoval: 22,
      confidence: 'MEDIUM'
    },
    canopyInterceptionRate: 0.16,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'MODERATE',
    typicalHeight: { min: 8, max: 15 },
    typicalCanopy: { min: 6, max: 12 },
    notes: 'Nitrogen-fixing, biofuel potential, coastal tolerance'
  },

  'Polyalthia longifolia': {
    scientificName: 'Polyalthia longifolia (Sonn.) Thwaites',
    commonNames: {
      english: 'Mast Tree, False Ashoka',
      hindi: 'Ashok',
      marathi: 'Ashok'
    },
    woodDensity: 0.58,
    woodDensitySource: 'Zanne et al. (2009)',
    woodDensityConfidence: 'MEDIUM',
    allometricMethod: 'FSI',
    carbonSequestrationRate: {
      young: 8,
      mature: 18,
      old: 15
    },
    oxygenProductionRate: {
      young: 50,
      mature: 95,
      old: 80
    },
    airPurification: {
      pm25RemovalRate: 3.6,
      pm10RemovalRate: 4.8,
      totalPollutantRemoval: 24,
      confidence: 'HIGH'
    },
    canopyInterceptionRate: 0.14,
    native: true,
    endemic: false,
    invasive: false,
    conservationStatus: 'LC',
    growthRate: 'FAST',
    typicalHeight: { min: 8, max: 16 },
    typicalCanopy: { min: 3, max: 6 },
    notes: 'Columnar form, excellent air purifier, ornamental'
  }
};

/**
 * Generic fallback for unknown species
 * Uses conservative estimates for tropical broadleaf trees
 */
export const GENERIC_SPECIES: SpeciesData = {
  scientificName: 'Unknown Species',
  commonNames: {
    english: 'Generic Tropical Tree'
  },
  woodDensity: 0.60, // Average for Indian tropical trees
  woodDensitySource: 'FSI (2019) average',
  woodDensityConfidence: 'MEDIUM',
  allometricMethod: 'FSI',
  carbonSequestrationRate: {
    young: 8,
    mature: 18,
    old: 15
  },
  oxygenProductionRate: {
    young: 50,
    mature: 95,
    old: 80
  },
  airPurification: {
    pm25RemovalRate: 3.0,
    pm10RemovalRate: 4.0,
    totalPollutantRemoval: 20,
    confidence: 'LOW'
  },
  canopyInterceptionRate: 0.18, // Conservative average
  native: false, // Unknown
  endemic: false,
  invasive: false,
  growthRate: 'MODERATE',
  typicalHeight: { min: 8, max: 15 },
  typicalCanopy: { min: 5, max: 12 },
  notes: 'Generic fallback values - species identification recommended'
};

/**
 * Helper function to get species data with fuzzy matching
 */
export function getSpeciesData(speciesName: string | undefined | null): SpeciesData {
  if (!speciesName || speciesName.trim() === '' || speciesName.toLowerCase() === 'unknown') {
    return GENERIC_SPECIES;
  }

  // Clean the species name
  const cleanName = speciesName.trim();

  // Direct match
  if (SPECIES_DATABASE[cleanName]) {
    return SPECIES_DATABASE[cleanName];
  }

  // Fuzzy matching - check if species name starts with any key
  const fuzzyMatch = Object.keys(SPECIES_DATABASE).find(key =>
    cleanName.toLowerCase().includes(key.toLowerCase().split(' ')[0]) || // Match genus
    key.toLowerCase().includes(cleanName.toLowerCase().split(' ')[0])
  );

  if (fuzzyMatch) {
    return SPECIES_DATABASE[fuzzyMatch];
  }

  // No match found - use generic
  return GENERIC_SPECIES;
}

/**
 * Get age category based on GBH (Girth at Breast Height)
 * Estimates tree age/maturity from girth measurement
 */
export function getAgeCategory(gbh: number): 'young' | 'mature' | 'old' {
  // Conservative age estimation based on GBH (cm)
  if (gbh < 60) return 'young';      // < 60 cm GBH (~10 years)
  if (gbh < 180) return 'mature';    // 60-180 cm GBH (10-40 years)
  return 'old';                       // > 180 cm GBH (40+ years)
}

/**
 * Calculate biomass using FSI allometric equation
 */
export function calculateBiomassFSI(
  dbh: number,      // Diameter at breast height (cm)
  height: number,   // Height (m)
  woodDensity: number // g/cm³
): number {
  // FSI Equation: AGB = 0.0509 × ρ × DBH² × H
  const agb = 0.0509 * woodDensity * Math.pow(dbh, 2) * height;
  return agb;
}

/**
 * Calculate biomass using Chave et al. (2014) pantropical equation
 */
export function calculateBiomassChave(
  dbh: number,
  height: number,
  woodDensity: number
): number {
  // Chave et al.: AGB = 0.0673 × (ρ × DBH² × H)^0.976
  const agb = 0.0673 * Math.pow(woodDensity * Math.pow(dbh, 2) * height, 0.976);
  return agb;
}

/**
 * Calculate carbon stored in tree
 */
export function calculateCarbonStored(biomass: number): {
  carbonKg: number;
  co2EquivalentKg: number;
} {
  // IPCC default: 47% of dry biomass is carbon
  const carbonKg = biomass * 0.47;

  // CO₂ equivalent (molecular weight ratio: CO₂/C = 44/12 = 3.67)
  const co2EquivalentKg = carbonKg * 3.67;

  return { carbonKg, co2EquivalentKg };
}

/**
 * Get confidence level for species calculations
 */
export function getCalculationConfidence(speciesData: SpeciesData): string {
  if (speciesData === GENERIC_SPECIES) {
    return 'LOW - Species identification needed for accurate values';
  }

  if (speciesData.woodDensityConfidence === 'HIGH' &&
      speciesData.airPurification.confidence === 'HIGH') {
    return 'HIGH - Well-documented species with validated data';
  }

  if (speciesData.woodDensityConfidence === 'MEDIUM' ||
      speciesData.airPurification.confidence === 'MEDIUM') {
    return 'MEDIUM - Some parameters adapted from similar species';
  }

  return 'LOW - Limited species-specific data available';
}

/**
 * Pune-specific environmental constants
 */
export const PUNE_CONSTANTS = {
  // Climate data from IMD Pune
  averageRainfallMm: 722,
  monsoonRainfallMm: 650,
  averageTemperatureC: 25.5,
  summerPeakTemperatureC: 38,

  // Air quality baseline (CPCB 2021-2023 data)
  averagePM25: 55, // μg/m³
  averagePM10: 95, // μg/m³
  averageNO2: 42,  // μg/m³

  // Urban context
  urbanDensityPeople: 6000, // per km²
  imperviousSurfacePercent: 65,

  // Human oxygen consumption (ICMR data)
  humanO2ConsumptionKgPerYear: 288,

  // Vehicle emission factors (Indian average)
  emissionFactors: {
    car: 0.18,          // kg CO₂/km
    motorbike: 0.08,    // kg CO₂/km
    autoRickshaw: 0.05, // kg CO₂/km (CNG)
    flight: 0.15        // kg CO₂/km (domestic)
  },

  // Everyday items emission factors (kg CO₂e)
  everydayEmissions: {
    // Clothing
    clothing: {
      tshirtCotton: 8,        // kg CO₂ per t-shirt (cotton)
      tshirtSynthetic: 10.4,  // kg CO₂ per t-shirt (synthetic blend)
      tshirtOrganic: 6.8      // kg CO₂ per t-shirt (organic cotton)
    },

    // Food (per kg)
    food: {
      beef: 27,        // kg CO₂/kg
      rice: 3.7,       // kg CO₂/kg
      chicken: 6.1,    // kg CO₂/kg
      vegetables: 0.5  // kg CO₂/kg (average)
    },

    // Electronics (per device)
    electronics: {
      smartphone: 75,  // kg CO₂ per device (manufacturing)
      laptop: 250      // kg CO₂ per device (manufacturing)
    },

    // Digital services (grams CO₂ per hour)
    streaming: {
      sd: 0.030,      // kg CO₂/hour (480p)
      hd: 0.045,      // kg CO₂/hour (1080p)
      fourK: 0.090    // kg CO₂/hour (2160p)
    },

    // Beverages (grams CO₂ per cup)
    beverages: {
      coffeeBlack: 0.021,    // kg CO₂/cup
      coffeeLatte: 0.340,    // kg CO₂/cup (with milk)
      teaBlack: 0.020,       // kg CO₂/cup
      teaMilk: 0.050         // kg CO₂/cup (with milk)
    }
  },

  // Electricity grid factor (India)
  electricityEmissionFactor: 0.716  // kg CO₂/kWh (FY 2022-23)
};

export default SPECIES_DATABASE;
